<template>
  <div>
    <div v-if="displayAutoLogoutMessage" class="inativity_message">
      <div class="left_section">
        <BaseIcon icon="$mdiAlertDecagramOutline" color="white" />
      </div>
      <div class="middle_section">
        <div class="section_title" v-text="$t('inactivity_message.title')" />

        <div class="message" v-text="$t('inactivity_message.message')" />
      </div>
      <div class="right_section">
        <BaseButtonIcon
          icon="$mdiClose"
          color="#000"
          @click="handleCloseAutoLogoutMessage()"
        />
      </div>
    </div>
    <BaseFrontOfficeTitle v-text="$t('wallet.signIn.title')" />
    <BaseFrontOfficeDivider type="grey" class="my-4" />
    <v-form>
      <BaseFrontOfficeTextField
        v-model="form.email"
        autofocus
        :tab-index="1"
        :label="`${$tc('utils.email', 1)}*`"
        :error-messages="emailError"
        placeholder="exemple@mail.com"
        @change="$v.form.email.$touch()"
        @blur="$v.form.email.$touch()"
      />
      <BaseFrontOfficeTextField
        v-model="form.password"
        :tab-index="2"
        :label="`${$t('account.password')}*`"
        :type="showPassword ? 'text' : 'password'"
        :error-messages="passwordErrors"
        :append-icon="showPasswordIcon"
        placeholder="•••••••••"
        :autofocus="$route.params.invitedUserEmail ? true : false"
        @click:append="showPassword = !showPassword"
        @change="$v.form.password.$touch()"
        @blur="$v.form.password.$touch()"
        @keydown.enter="submit"
      />
      <BaseFrontOfficeAlert v-if="submitError" type="error">
        {{ submitError }}
      </BaseFrontOfficeAlert>
      <div class="d-flex justify-start mb-4">
        <BaseFrontOfficeHyperlink
          v-if="$route.fullPath.indexOf('/wallet') != -1"
          :to="{
            name: 'WalletPasswordReset',
            query: {
              next: $route.query.next,
            },
            params: {
              accessCampaign: $route.params.accessCampaign,
            },
          }"
        >
          {{ $t("auth.reset.forgottenPassword") }}
        </BaseFrontOfficeHyperlink>
        <BaseFrontOfficeHyperlink
          v-else
          :to="{
            name: 'PasswordReset',
          }"
        >
          {{ $t("auth.reset.forgottenPassword") }}
        </BaseFrontOfficeHyperlink>
      </div>
      <div class="button-container">
        <BaseFrontOfficeButton
          tabindex="3"
          class="ml-auto"
          icon="$mdiCheck"
          :disabled="$v.form.$invalid"
          @click="submit()"
        >
          {{ $t("wallet.signIn.submit") }}
        </BaseFrontOfficeButton>
        <BaseFrontOfficeButton
          class="ml-auto connect"
          tabindex="4"
          @click.prevent="expanded = !expanded"
        >
          <div class="d-flex align-center action">
            <BaseImage
              :src="$findStatic('microsoftLogo')"
              height="24px"
              width="24px"
            />
            <span class="ml-3">
              {{ $t("wallet.signIn.submitMicrosoft") }}
            </span>
            <div>
              <BaseIcon
                :icon="expanded ? '$mdiChevronUp' : '$mdiChevronDown'"
                color="white"
              />
            </div>
            <div v-show="expanded && SSOLists.length" class="menu__sso">
              <div
                v-for="item in SSOLists"
                :key="item.aadSlug"
                class="list__item"
                @click="connectMicrocrosft(item.aadUrlAuthorize)"
              >
                {{ item.aadSlug }}
              </div>
            </div>
          </div>
        </BaseFrontOfficeButton>
      </div>
      <HasToRegister />
    </v-form>
  </div>
</template>

<script>
import HasToRegister from "../Components/HasToRegister.vue";
import { getToken } from "@/core/Api/cosmoAxiosInstance";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
const errorCodes = {
  ORGANISATION_NOT_AUTHORIZED: "organization-not-authorized-for-sso",
  NOT_USER_COSMO: "user-not-found-in-cosmo",
  NOT_REGISTERED: "user-not-registered-yet-in-cosmo",
  ERROR_OAUTH: "oauth-application-not-found",
};

export default {
  name: "LoginPage",
  components: { HasToRegister },
  mixins: [validationMixin],
  props: {
    accessCampaign: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      displayAutoLogoutMessage: false,
      isLoading: false,
      form: {
        email: "",
        password: "",
      },
      showPassword: false,
      submitError: "",
      expanded: false,
      SSOLists: [],
    };
  },
  validations: {
    form: {
      email: {
        required,
        email,
      },
      password: {
        required,
      },
    },
  },
  computed: {
    // password icon
    showPasswordIcon() {
      if (this.form.password === "") return "";
      return this.showPassword ? "$mdiEye" : "$mdiEyeOff";
    },
    ...mapState({
      activeUser: (state) => state.account.user,
    }),
    //#region Form errors
    emailError() {
      if (!this.$v.form.email.$dirty) {
        return "";
      }
      if (!this.$v.form.email.required) {
        return this.$t("wallet.signup.errors.emailRequired");
      }
      if (!this.$v.form.email.email) {
        return this.$t("folder.askInformationForm.validation.emailInvalid");
      }
      return "";
    },
    passwordErrors() {
      if (!this.$v.form.password.$dirty) {
        return "";
      }
      if (!this.$v.form.password.required) {
        return this.$t("wallet.signup.errors.passwordRequired");
      }
      return "";
    },
    //#endregion
  },
  watch: {
    "$route.query.sso_error": {
      handler() {
        if (!this.$route.query.sso_error) return;
        let msg = "";
        switch (this.$route.query.sso_error) {
          case errorCodes.ERROR_OAUTH:
            msg = this.$t("utils.sso.oauthError");
            break;
          case errorCodes.NOT_REGISTERED:
            msg = this.$t("utils.sso.userNonregistered");
            break;
          case errorCodes.NOT_USER_COSMO:
            msg = this.$t("utils.sso.nonUser");
            break;
          case errorCodes.ORGANISATION_NOT_AUTHORIZED:
            msg = this.$t("utils.sso.organizationNonAuthorized");
            break;
        }
        this.$store.dispatch("snackbar/active", {
          message: msg,
          type: "Error",
          needClose: false,
        });
      },
      immediate: true,
    },
  },
  created() {
    this.checkAutoLogout();
    this.initialize();
    if (this.$route.params.invitedUserEmail) {
      this.form.email = this.$route.params.invitedUserEmail;
    } else if (this.$route.query?.email) {
      this.form.email = this.$route.query.email;
    }
  },
  methods: {
    async initialize() {
      this.SSOLists = await this.$store.dispatch("organization/getSSOLists");
    },
    connectMicrocrosft(url) {
      open(url);
    },
    async submit() {
      this.$v.form.$touch();
      this.submitError = "";
      if (this.$v.form.$invalid) return;
      this.isLoading = true;
      try {
        await getToken(this.form.email, this.form.password);
        await this.$store.dispatch("account/fetchUser");

        this.$store.commit("core/SET_HAS_LOGGED_NORMALY", true);

        if (this.$route.query.next)
          return this.$router.push(this.$route.query.next);
        return this.$router.push({
          name: "dashboard",
        });
      } catch (error) {
        if (error.response.data.error === "invalid_grant") {
          this.submitError = this.$t("wallet.signIn.errorInvalidGrant");
        }
        if (error?.response) {
          this.$store.dispatch("snackbar/active", {
            message: error.response.data.error_description,
            type: "ERROR",
          });
        }
      }
    },
    checkAutoLogout() {
      const autoLoggedOut = localStorage.getItem("autoLoggedOut");
      if (!autoLoggedOut) return;
      this.displayAutoLogoutMessage = true;
    },
    handleCloseAutoLogoutMessage() {
      this.displayAutoLogoutMessage = false;
      localStorage.removeItem("autoLoggedOut");
    },
  },
};
</script>
<style lang="scss" scoped>
.inativity_message {
  position: fixed;
  z-index: 10000000;
  top: 20px;
  left: 20px;
  width: calc(100vw - 40px);
  display: flex;
  padding: 16px;
  border-radius: 5px;
  background: #fff;
  gap: 16px;
  margin-bottom: 24px;
  &::after {
    content: "";
    position: absolute;
    z-index: -2;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(#fc001d, 0.1);
  }
  .left_section {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 5px;
    background: #fc001d;
    display: flex;
    justify-content: center;
    align-items: center;
    ::v-deep {
      .v-icon {
        width: 20px;
        height: 20px;
      }
    }
  }
  .middle_section {
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    .section_title {
      color: #000;
      font-size: 16px;
      line-height: 22px;
    }
    .section_sub_title {
      color: #353542;
    }
    .message {
      color: #707080;
    }
  }
}
.button-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.action {
  gap: 16px;
  padding: 13px 24px;
}
.connect {
  position: relative;
  .menu__sso {
    position: absolute;
    width: 190px;
    padding: 8px;
    background: #f4f7ff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    right: 0;
    bottom: 0;
    transform: translate(16px, calc(100% + 4px));
    .list__item {
      height: 32px;
      padding: 3px 8px;
      color: var(--Light---primary-text-color, #323338);
      /* Bouton/Base */
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      border-radius: 4px;
      display: flex;
      align-items: center;
    }
  }
  .list__item:hover {
    background: #eeeef7;
  }
}
</style>
